import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import OnboardingContainer from "./container";
import { useUpdateProfileMutation } from "pages/profile/services";
import { setPOSData, removePOSData } from "pages/login/services";
import { useUploadImageMutation } from "pages/categories/services";
import toast from "react-hot-toast";

const Onboarding = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const posData = useSelector((state) => state?.posData);
    const vendorData = posData?.vendor;
    const initialValuesRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(2);
    const [isSuccessScreen, setIsSuccessScreen] = useState(false);

    const [updateProfile, { isLoading: isSubmittingProfile }] = useUpdateProfileMutation();
    const [uploadImage] = useUploadImageMutation();

    const selectedCategoryNames = Array.isArray(vendorData?.selected_categories)
        ? vendorData.selected_categories.map((c) => c.name).join(", ")
        : "";

    // Stepper fields as required
    const initialValues = {
        // Step 2 details
        name: vendorData?.name || "",
        categoryNames: selectedCategoryNames || "",
        tagline: vendorData?.tagline || "",
        restaurant_website: vendorData?.restaurant_website || "",
        description: vendorData?.description || "",
        service_tax: vendorData?.service_tax || "",
        is_vat_enabled: !!vendorData?.vat_number,
        vat_number: vendorData?.vat_number || "",
        image: vendorData?.image || null,
        banner_image: vendorData?.banner_image || null,
        imageFile: null,
        bannerImageFile: null,

        // Step 3 details (Location)
        title: "",
        address: vendorData?.locations?.address || "",
        address_line2: vendorData?.locations?.address_line2 || "",
        city: vendorData?.locations?.city || "",
        state: vendorData?.locations?.state || "",
        country: vendorData?.locations?.country || "United Kingdom",
        postal_code: vendorData?.locations?.postal_code || "",
        lat: vendorData?.locations?.lat || "",
        long: vendorData?.locations?.long || "",

        // Step 4 details (Contact)
        full_name: posData?.full_name || "",
        personal_email: posData?.personal_email || "",
        phone: posData?.phone || "",
        alternate_contact_number: posData?.alternate_contact_number || "",
        country_code: posData?.country_code || "44",
        country_ISO: posData?.country_ISO || "GB",

        // Add additional required keys just to ensure they aren't lost
        currency: vendorData?.currency || "EURO",
        platform_fee: vendorData?.platform_fee || "",
    };

    if (!initialValuesRef.current) {
        initialValuesRef.current = initialValues;
    }

    // Step-by-step validations are also supported but here we have Yup validation schema
    const validationSchema = Yup.object({
        // Step 2 details
        image: Yup.mixed().nullable().required("Profile Image is required"),
        banner_image: Yup.mixed().nullable(),
        service_tax: Yup.string().required("Service Tax (%) is required"),
        vat_number: Yup.string().when("is_vat_enabled", {
            is: true,
            then: (schema) => schema.required("VAT Number is required"),
            otherwise: (schema) => schema.notRequired()
        }),

        // Step 3 details
        title: Yup.string().required("Premises Name is required"),
        address: Yup.string().required("Address Line 1 is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        country: Yup.string().required("Country is required"),
        postal_code: Yup.string().required("Postal Code is required"),

        // Step 4 details
        full_name: Yup.string().required("Owner's Full Name is required"),
        personal_email: Yup.string().email("Invalid email").required("Owner's Email is required"),
        phone: Yup.string().required("Owner's Contact Number is required"),
        alternate_contact_number: Yup.string().nullable(),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                let imageId = values.image;
                let bannerImageId = values.banner_image;

                if (!imageId) {
                    toast.error("Please upload a Profile Image");
                    return;
                }

                // 1. Upload Profile Image if changed/selected as file
                if (values.imageFile) {
                    const formData = new FormData();
                    formData.append("file", values.imageFile);
                    const imageResponse = await uploadImage(formData).unwrap();
                    imageId = imageResponse?.data;
                }

                // 2. Upload Banner Image if changed/selected as file
                if (values.bannerImageFile) {
                    const formData = new FormData();
                    formData.append("file", values.bannerImageFile);
                    const bannerResponse = await uploadImage(formData).unwrap();
                    bannerImageId = bannerResponse?.data;
                }

                const cleanCountryCode = values?.country_code?.toString().replace(/^\+/, "");

                // Default banner image dummy value if not uploaded or provided
                const DEFAULT_BANNER_IMAGE = "1792579941150-1.png";
                let finalBannerImage = DEFAULT_BANNER_IMAGE;
                if (bannerImageId && !bannerImageId.startsWith("blob:")) {
                    finalBannerImage = bannerImageId;
                }

                // 3. Prepare JSON payload
                const payload = {
                    ...values,
                    image: imageId,
                    banner_image: finalBannerImage,
                    country_code: cleanCountryCode,
                    vendorId: vendorData?.id
                };

                // Remove temporary file objects
                delete payload.imageFile;
                delete payload.bannerImageFile;
                delete payload.categoryNames;

                const response = await updateProfile(payload).unwrap();

                if (response.status == 200 || response.status == 201) {
                    setIsSuccessScreen(true);
                    // toast.success("Profile updated successfully!");

                    // Wait 2 seconds, then update redux and navigate to home
                    setTimeout(() => {
                        const responseData = response.data || posData;
                        const updatedUser = {
                            ...responseData,
                            is_profile_completed: true,
                            vendor: {
                                ...(responseData.vendor || {}),
                                is_profile_completed: true
                            }
                        };
                        dispatch(setPOSData({ ...updatedUser, token: posData?.token }));
                        navigate('/');
                    }, 2000);
                } else {
                    toast.error(response?.message || "Failed to update profile");
                }
            } catch (error) {
                console.error("Failed to update profile during onboarding:", error);
                // toast.error(error?.data?.message || error?.message || "Something went wrong. Please try again.");
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleFileUpload = (field, acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            if (field === "image") {
                formik.setFieldValue("imageFile", file);
                formik.setFieldValue("image", URL.createObjectURL(file));
                formik.setFieldTouched("image", true);
            } else if (field === "banner_image") {
                formik.setFieldValue("bannerImageFile", file);
                formik.setFieldValue("banner_image", URL.createObjectURL(file));
                formik.setFieldTouched("banner_image", true);
            }
        }
    };

    const handleLogout = () => {
        dispatch(removePOSData());
        navigate("/login");
    };

    return (
        <OnboardingContainer
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            formik={formik}
            handleFileUpload={handleFileUpload}
            handleLogout={handleLogout}
            isSubmittingProfile={isSubmittingProfile}
            isSuccessScreen={isSuccessScreen}
        />
    );
};

export default Onboarding;
